import { useEffect, useState, useCallback } from "react";
import { api, apiErr } from "../lib/api";
import { money } from "../lib/format";
import { toast } from "sonner";
import { Save, History, Plus, Trash2 } from "lucide-react";
import OpeningDeductionYtd from "./OpeningDeductionYtd";
import { useClientPerms } from "../lib/useClientPerms";

const GROUPS = [
  { group: "Earnings", fields: [["gross_employment_income", "Gross employment income (YTD)"], ["taxable_employment_income", "Taxable employment income (YTD)"]] },
  { group: "CPP", fields: [["cpp_pensionable_earnings", "CPP pensionable earnings"], ["cpp_employee", "CPP contributions (employee)"], ["cpp_employer", "CPP contributions (employer)"]] },
  { group: "CPP2", fields: [["cpp2_pensionable_earnings", "CPP2 pensionable earnings"], ["cpp2_employee", "CPP2 contributions (employee)"], ["cpp2_employer", "CPP2 contributions (employer)"]] },
  { group: "EI", fields: [["ei_insurable_earnings", "EI insurable earnings"], ["ei_employee", "EI premiums (employee)"], ["ei_employer", "EI premiums (employer)"]] },
  { group: "Tax", fields: [["income_tax", "Income tax deducted (YTD)"]] },
  { group: "Vacation", fields: [["vacation_earned", "Vacation earned (YTD)"], ["vacation_paid", "Vacation paid (YTD)"], ["vacation_balance", "Vacation balance (opening)"]] },
  { group: "Other", fields: [["net_pay_ytd", "Net pay (YTD)"], ["insurable_hours", "Insurable hours (YTD)"], ["union_dues", "Union dues (YTD)"], ["employee_rpp", "RPP contributions (YTD)"], ["rrsp", "RRSP (YTD)"], ["taxable_benefits", "Taxable benefits (YTD)"]] },
];
const SOURCES = ["QuickBooks Payroll", "Wagepoint", "ADP", "Ceridian", "Payworks", "Manual", "Other"];
const YEARS = [2024, 2025, 2026];

export default function OpeningBalancesTab({ companyId, employee, canEdit }) {
  const canDelete = !useClientPerms().isClient;
  const eid = employee?.employee_id;
  const [year, setYear] = useState(2026);
  const [existing, setExisting] = useState(null);
  const [recon, setRecon] = useState(null);
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState({ effective_as_of_date: "", source_system: "Manual", source_reference: "", notes: "", amounts: {}, t4_boxes: [], reason: "" });
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [newHire, setNewHire] = useState(null);
  const [nhNote, setNhNote] = useState("");

  const load = useCallback(() => {
    if (!companyId || !eid) return;
    api.get(`/companies/${companyId}/opening-balances?employee_id=${eid}&tax_year=${year}`).then((r) => {
      const doc = (r.data || [])[0] || null;
      setExisting(doc);
      setForm({
        effective_as_of_date: doc?.effective_as_of_date || "",
        source_system: doc?.source_system || "Manual",
        source_reference: doc?.source_reference || "",
        notes: doc?.notes || "",
        amounts: { ...(doc?.amounts || {}) },
        t4_boxes: [...(doc?.t4_boxes || [])],
        reason: "",
      });
    }).catch(() => setExisting(null));
    api.get(`/companies/${companyId}/opening-balances/${eid}/reconciliation?tax_year=${year}`).then((r) => setRecon(r.data)).catch(() => setRecon(null));
    api.get(`/companies/${companyId}/opening-balances?employee_id=${eid}&tax_year=${year}&all_status=true`).then((r) => setHistory(r.data || [])).catch(() => setHistory([]));
    api.get(`/companies/${companyId}/new-hire-confirmations?tax_year=${year}`).then((r) => setNewHire((r.data || []).find((n) => n.employee_id === eid) || null)).catch(() => setNewHire(null));
  }, [companyId, eid, year]);
  useEffect(() => { load(); }, [load]);

  const confirmNewHire = async () => {
    try {
      await api.post(`/companies/${companyId}/employees/${eid}/confirm-new-hire`, { tax_year: year, note: nhNote });
      toast.success("Confirmed as a new hire (no prior payroll this year)"); setNhNote(""); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const unconfirmNewHire = async () => {
    try {
      await api.delete(`/companies/${companyId}/employees/${eid}/confirm-new-hire?tax_year=${year}`);
      toast.success("New-hire confirmation removed"); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const setAmt = (k) => (e) => setForm((f) => ({ ...f, amounts: { ...f.amounts, [k]: e.target.value === "" ? "" : Number(e.target.value) } }));
  const setMeta = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setBox = (i, field, val) => setForm((f) => { const a = [...f.t4_boxes]; a[i] = { ...a[i], [field]: field === "amount" ? (val === "" ? "" : Number(val)) : val }; return { ...f, t4_boxes: a }; });
  const addBox = () => setForm((f) => ({ ...f, t4_boxes: [...f.t4_boxes, { code: "", amount: "" }] }));
  const rmBox = (i) => setForm((f) => ({ ...f, t4_boxes: f.t4_boxes.filter((_, x) => x !== i) }));

  const save = async () => {
    if (existing && !form.reason.trim()) { toast.error("A reason is required to change an existing opening balance."); return; }
    setBusy(true);
    try {
      const amounts = {};
      Object.entries(form.amounts).forEach(([k, v]) => { if (v !== "" && v != null && !Number.isNaN(Number(v))) amounts[k] = Number(v); });
      const t4_boxes = form.t4_boxes.filter((b) => String(b.code || "").trim() !== "").map((b) => ({ code: String(b.code).trim(), amount: Number(b.amount || 0) }));
      const r = await api.post(`/companies/${companyId}/opening-balances`, {
        employee_id: eid, tax_year: year, effective_as_of_date: form.effective_as_of_date,
        source_system: form.source_system, source_reference: form.source_reference, notes: form.notes,
        amounts, t4_boxes, reason: form.reason,
      });
      const warns = r.data?.warnings || [];
      toast.success("Opening balance saved" + (warns.length ? ` — ${warns.length} warning(s)` : ""));
      warns.forEach((w) => toast.warning(w.message));
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const Room = ({ label, node }) => (
    <div className="border border-border p-2.5">
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label} remaining</div>
      <div className="text-lg font-black tabular-nums">{node?.remaining == null ? "—" : money(node.remaining)}</div>
      <div className="text-[11px] text-muted-foreground">of {node?.max == null ? "—" : money(node.max)} · used {money(node?.ytd || 0)}</div>
    </div>
  );

  return (
    <div className="py-2 space-y-4" data-testid="opening-balances-tab">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground block mb-1">Tax year</label>
          <select data-testid="ob-year-select" value={year} onChange={(e) => setYear(Number(e.target.value))} className="border border-input bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-black">
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        {existing ? (
          <span className="text-xs px-2 py-1 bg-emerald-100 text-emerald-700 font-medium" data-testid="ob-status-active">Active opening balance · {existing.source_system}</span>
        ) : (
          <span className="text-xs px-2 py-1 bg-neutral-100 text-muted-foreground" data-testid="ob-status-none">No opening balance for {year}</span>
        )}
        {history.length > 0 && (
          <button data-testid="ob-history-toggle" onClick={() => setShowHistory((s) => !s)} className="ml-auto inline-flex items-center gap-1.5 text-xs border border-border px-2.5 py-1.5 hover:border-black transition-colors">
            <History className="h-3.5 w-3.5" /> Audit history ({history.length})
          </button>
        )}
      </div>

      {recon && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="ob-reconciliation">
          <div className="border border-border p-2.5">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Vacation opening</div>
            <div className="text-lg font-black tabular-nums">{money(recon.vacation_balance || 0)}</div>
          </div>
          <Room label="CPP" node={recon.cpp} />
          <Room label="CPP2" node={recon.cpp2} />
          <Room label="EI" node={recon.ei} />
        </div>
      )}
      {recon && !recon.ruleset_available && (
        <div className="text-xs border-l-2 border-amber-400 bg-amber-50 px-3 py-2 text-amber-800" data-testid="ob-no-ruleset">
          No {year} ruleset on file — statutory maximum/room checks are unavailable. Balances are stored as reported.
        </div>
      )}

      {!existing && canEdit && (
        <div className="border border-border bg-neutral-50 p-3" data-testid="ob-new-hire-block">
          {newHire ? (
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className="px-2 py-1 bg-emerald-100 text-emerald-700 font-medium" data-testid="ob-new-hire-confirmed">New hire confirmed — no prior {year} payroll</span>
              <span className="text-xs text-muted-foreground">by {newHire.confirmed_by} · {String(newHire.confirmed_at).slice(0, 10)}{newHire.note ? ` · ${newHire.note}` : ""}</span>
              {canDelete && <button data-testid="ob-new-hire-remove" onClick={unconfirmNewHire} className="ml-auto text-xs border border-border px-2.5 py-1 hover:border-red-600 hover:text-red-600 transition-colors">Remove</button>}
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm">No opening balance. If this employee had <b>no prior payroll</b> in {year}, confirm them as a new hire:</span>
              <input data-testid="ob-new-hire-note" value={nhNote} onChange={(e) => setNhNote(e.target.value)} placeholder="Optional note" className="border border-input bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
              <button data-testid="ob-new-hire-confirm" onClick={confirmNewHire} className="inline-flex items-center gap-1.5 bg-black text-white px-3 py-1.5 text-sm font-medium hover:opacity-90 transition-opacity">Confirm new hire</button>
            </div>
          )}
        </div>
      )}

      {showHistory && (
        <div className="border border-border" data-testid="ob-audit-list">
          <div className="bg-neutral-50 px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Audit history</div>
          {history.map((h) => (
            <div key={h.ob_id} className="px-3 py-2 border-t border-border/60 text-xs">
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                <span className={`px-1.5 py-0.5 ${h.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-neutral-100 text-muted-foreground"}`}>{h.status}</span>
                <span>{h.source_system}</span>
                <span className="text-muted-foreground">by {h.updated_by || h.created_by} · {String(h.updated_at || h.created_at).slice(0, 10)}</span>
                {(h.audit || []).length > 0 && <span className="text-muted-foreground">· {h.audit.length} edit(s)</span>}
                {h.import_batch_id && <span className="text-muted-foreground">· batch {h.import_batch_id}</span>}
              </div>
              {(h.audit || []).map((a, ai) => (
                (a.t4_box_changes || []).length > 0 && (
                  <div key={ai} className="mt-1 pl-3 text-[11px] text-muted-foreground" data-testid="ob-t4-audit">
                    T4 box changes ({a.reason || a.source}): {a.t4_box_changes.map((b) => `Box ${b.code}: ${b.previous ?? "—"}→${b.new ?? "removed"}`).join("; ")}
                  </div>
                )
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Manual entry, grouped */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground block mb-1">Effective as-of date</label>
            <input data-testid="ob-effective-date" type="date" disabled={!canEdit} value={form.effective_as_of_date} onChange={setMeta("effective_as_of_date")} className="w-full border border-input bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-black disabled:bg-neutral-100" />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground block mb-1">Source system</label>
            <select data-testid="ob-source-system" disabled={!canEdit} value={form.source_system} onChange={setMeta("source_system")} className="w-full border border-input bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-black disabled:bg-neutral-100">
              {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground block mb-1">Source reference</label>
            <input data-testid="ob-source-reference" disabled={!canEdit} value={form.source_reference} onChange={setMeta("source_reference")} placeholder="e.g. QB export Jun-2026" className="w-full border border-input bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-black disabled:bg-neutral-100" />
          </div>
        </div>

        {GROUPS.map((g) => (
          <div key={g.group} className="border border-border">
            <div className="bg-neutral-50 px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{g.group}</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-3">
              {g.fields.map(([k, label]) => (
                <div key={k}>
                  <label className="text-xs text-muted-foreground block mb-1">{label}</label>
                  <input data-testid={`ob-amt-${k}`} type="number" step="0.01" disabled={!canEdit} value={form.amounts[k] ?? ""} onChange={setAmt(k)} className="w-full border border-input bg-white px-2 py-1.5 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-black disabled:bg-neutral-100" />
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="border border-border">
          <div className="bg-neutral-50 px-3 py-1.5 flex items-center justify-between">
            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">T4 boxes (custom / other information)</span>
            {canEdit && <button data-testid="ob-add-t4box" onClick={addBox} className="inline-flex items-center gap-1 text-xs border border-border px-2 py-1 hover:border-black transition-colors"><Plus className="h-3 w-3" /> Add box</button>}
          </div>
          <div className="p-3 space-y-2">
            <p className="text-[11px] text-muted-foreground">Standard boxes (14/16/16A/18/22/24/26) are derived automatically from the fields above — do not re-enter them here or they will be flagged as a conflict.</p>
            {form.t4_boxes.map((b, i) => (
              <div key={i} className="flex items-center gap-2">
                <input data-testid={`ob-t4box-code-${i}`} disabled={!canEdit} value={b.code} onChange={(e) => setBox(i, "code", e.target.value)} placeholder="Box code (e.g. 40)" className="w-40 border border-input bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-black disabled:bg-neutral-100" />
                <input data-testid={`ob-t4box-amt-${i}`} type="number" step="0.01" disabled={!canEdit} value={b.amount} onChange={(e) => setBox(i, "amount", e.target.value)} placeholder="Amount" className="w-40 border border-input bg-white px-2 py-1.5 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-black disabled:bg-neutral-100" />
                {canEdit && <button data-testid={`ob-t4box-rm-${i}`} onClick={() => rmBox(i)} className="text-red-600 hover:text-red-800"><Trash2 className="h-4 w-4" /></button>}
              </div>
            ))}
          </div>
        </div>

        <div>
          <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground block mb-1">Notes</label>
          <textarea data-testid="ob-notes" disabled={!canEdit} value={form.notes} onChange={setMeta("notes")} rows={2} className="w-full border border-input bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-black disabled:bg-neutral-100" />
        </div>

        {canEdit && existing && (
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-amber-700 block mb-1">Reason for change (required)</label>
            <input data-testid="ob-reason" value={form.reason} onChange={setMeta("reason")} placeholder="Why is this balance being corrected?" className="w-full border border-amber-300 bg-amber-50 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-black" />
          </div>
        )}

        {canEdit && (
          <button data-testid="ob-save" onClick={save} disabled={busy} className="inline-flex items-center gap-2 bg-black text-white px-4 py-2 text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50">
            <Save className="h-4 w-4" /> {busy ? "Saving…" : existing ? "Save correction" : "Save opening balance"}
          </button>
        )}

        <OpeningDeductionYtd companyId={companyId} employee={employee} canEdit={canEdit} year={year} />
      </div>
    </div>
  );
}
